import { ISaSmsAutoRecharge } from '@interfaces/sa-sms';
import { convertPrice } from '@utils/resource';
import { Alert, Button, Card, Descriptions, Form, InputNumber, notification, Switch } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Values = { adminDisabled: boolean; maxCapUsd: number | null };
type Props = {
    tenantId: string;
    settings: ISaSmsAutoRecharge;
    canEdit: boolean;
    onChanged: () => void;
};

/** Admin override of the tenant's auto-recharge: disable it and / or cap the monthly spend (USD in the form, cents on the wire). */
export const SmsAutoRechargeAdmin: React.FC<Props> = ({
    tenantId,
    settings,
    canEdit,
    onChanged,
}) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [open, setOpen] = useState(false);

    const confirm = async ({ reason, adminDisabled, maxCapUsd }: Values & { reason: string }) => {
        await send('put', `/tenants/${tenantId}/auto-recharge`, {
            adminDisabled,
            adminMaxCap: maxCapUsd ? Math.round(maxCapUsd * 100) : null,
            reason,
        });
        notification.success({ message: t('saved') });
        setOpen(false);
        onChanged();
    };

    return (
        <Card
            title={t('credits.autoRecharge')}
            extra={canEdit && <Button onClick={() => setOpen(true)}>{t('actions.edit')}</Button>}
        >
            {settings.lastFailure && (
                <Alert
                    type="warning"
                    showIcon
                    message={settings.lastFailure}
                    style={{ marginBottom: 12 }}
                />
            )}
            <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={t('credits.autoEnabled')}>
                    {settings.enabled ? t('common.on') : t('common.off')}
                </Descriptions.Item>
                <Descriptions.Item label={t('credits.autoThreshold')}>
                    {settings.threshold ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('credits.autoCap')}>
                    {settings.monthlyCap ? convertPrice(settings.monthlyCap) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('credits.adminDisabled')}>
                    {settings.adminDisabled ? t('common.yes') : t('common.no')}
                </Descriptions.Item>
                <Descriptions.Item label={t('credits.adminMaxCap')}>
                    {settings.adminMaxCap ? convertPrice(settings.adminMaxCap) : '-'}
                </Descriptions.Item>
            </Descriptions>
            <SaSmsReasonModal<Values>
                open={open}
                title={t('credits.autoRecharge')}
                initialValues={{
                    adminDisabled: settings.adminDisabled,
                    maxCapUsd: settings.adminMaxCap ? settings.adminMaxCap / 100 : null,
                }}
                onCancel={() => setOpen(false)}
                onConfirm={confirm}
            >
                <Form.Item
                    name="adminDisabled"
                    label={t('credits.adminDisabled')}
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    name="maxCapUsd"
                    label={t('credits.adminMaxCapUsd')}
                    extra={t('credits.blankNoCeiling')}
                >
                    <InputNumber
                        min={1}
                        max={100000}
                        precision={2}
                        prefix="$"
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            </SaSmsReasonModal>
        </Card>
    );
};
